/**
 * UpdateBanner - Shows when a new version is available
 *
 * Only renders in desktop (Tauri) mode.
 * Displays release notes and provides update/dismiss actions.
 *
 * States:
 * - available:   Banner with version + notes + "Update Now" button
 * - downloading: Progress bar with percentage
 * - ready:       "Restart to apply" button
 * - error:       Error message (auto-dismisses)
 * - idle/checking/up-to-date: hidden
 */

import { useState, useEffect } from 'react';
import { Button } from '@pyxl/ui';
import {
  getUpdateStatus,
  onUpdateStatusChange,
  downloadAndInstall,
  type UpdateStatus,
} from '../lib/updater';
import { isDesktop } from '../lib/db/dataLayer';
import './UpdateBanner.css';

export function UpdateBanner() {
  const [status, setStatus] = useState<UpdateStatus>({ type: 'idle' });
  const [dismissed, setDismissed] = useState(false);
  const [desktop, setDesktop] = useState(false);

  useEffect(() => {
    const isDesktopMode = isDesktop();
    setDesktop(isDesktopMode);
    if (!isDesktopMode) return;

    setStatus(getUpdateStatus());
    const unsub = onUpdateStatusChange(setStatus);
    return unsub;
  }, []);

  // Don't render in web mode
  if (!desktop) return null;

  // Don't render if dismissed or no update
  if (dismissed) return null;
  if (status.type === 'idle' || status.type === 'checking' || status.type === 'up-to-date') {
    return null;
  }

  if (status.type === 'available') {
    return (
      <div className="update-banner" role="alert">
        <div className="update-banner__content">
          <div className="update-banner__icon">🚀</div>
          <div className="update-banner__text">
            <strong>pyxl {status.info.version}</strong> is available
            {status.info.body && (
              <span className="update-banner__notes">
                {' — '}
                {truncateNotes(status.info.body)}
              </span>
            )}
          </div>
        </div>
        <div className="update-banner__actions">
          <Button variant="primary" size="sm" onClick={() => downloadAndInstall()}>
            Update Now
          </Button>
          <button
            className="update-banner__dismiss"
            onClick={() => setDismissed(true)}
            aria-label="Dismiss update notification"
          >
            ✕
          </button>
        </div>
      </div>
    );
  }

  if (status.type === 'downloading') {
    return (
      <div className="update-banner update-banner--downloading" role="status">
        <div className="update-banner__content">
          <div className="update-banner__icon">⬇</div>
          <div className="update-banner__text">
            Downloading update... {status.progress}%
          </div>
        </div>
        <div className="update-banner__progress">
          <div
            className="update-banner__progress-bar"
            style={{ width: `${status.progress}%` }}
          />
        </div>
      </div>
    );
  }

  if (status.type === 'ready') {
    return (
      <div className="update-banner update-banner--ready" role="alert">
        <div className="update-banner__content">
          <div className="update-banner__icon">✅</div>
          <div className="update-banner__text">
            Update downloaded. Restart to apply.
          </div>
        </div>
      </div>
    );
  }

  if (status.type === 'error') {
    return (
      <div className="update-banner update-banner--error" role="alert">
        <div className="update-banner__content">
          <div className="update-banner__icon">⚠</div>
          <div className="update-banner__text">
            Update failed: {status.message}
          </div>
        </div>
        <button
          className="update-banner__dismiss"
          onClick={() => setDismissed(true)}
          aria-label="Dismiss error"
        >
          ✕
        </button>
      </div>
    );
  }

  return null;
}

/**
 * Truncate release notes to a single line summary.
 */
function truncateNotes(body: string): string {
  // Take first non-empty line, strip markdown
  const firstLine = body
    .split('\n')
    .map((l) => l.trim())
    .find((l) => l.length > 0 && !l.startsWith('#'));

  if (!firstLine) return '';

  // Strip markdown formatting
  const clean = firstLine
    .replace(/\*\*/g, '')
    .replace(/\*/g, '')
    .replace(/`/g, '')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');

  return clean.length > 80 ? clean.slice(0, 77) + '...' : clean;
}
